import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js'
import options from "./js/options.js"
import light from "./js/light.js"
import loadingManager from "./js/loader.js"
import { pdfCreate } from "./js/pdf.js"
import { EffectComposer, RenderPass, SMAAPass, OutputPass } from "three/addons"
import theme from "./js/theme.js"
import { GUI } from 'three/examples/jsm/libs/lil-gui.module.min.js'
import {initAIImageGenerator} from "./js/ai.js";

// ─── СЦЕНА ───────────────────────────────────

const canvasContainer = document.querySelector('#canvas-container')
const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()
const isLarge = canvasContainer.getBoundingClientRect().width > 600

const sizes = {
    width: canvasContainer.getBoundingClientRect().width,
    height: canvasContainer.getBoundingClientRect().height
}

const pixelRatio = Math.min(window.devicePixelRatio, 2)

// ─── ЗАГРУЗЧИКИ ───────────────────────────────

const dracoLoader = new DRACOLoader(loadingManager)
dracoLoader.setDecoderPath('./draco/')
const gltfLoader = new GLTFLoader(loadingManager)
gltfLoader.setDRACOLoader(dracoLoader)

// ─── КАМЕРА ───────────────────────────────────

const camera = new THREE.PerspectiveCamera(38, sizes.width / sizes.height, 0.3, 100)
isLarge ? camera.position.set(6, 3.5, 1.5) : camera.position.set(8, 8, 12)
scene.add(camera)

// ─── КОНТРОЛЫ ─────────────────────────────────

const controls = new OrbitControls(camera, canvas)
controls.target.set(0, 1, 0)
controls.enableDamping = true
controls.dampingFactor = 0.04
controls.enablePan = false
controls.minDistance = 1
controls.maxDistance = isLarge ? 10 : 18
controls.maxPolarAngle = Math.PI / 2
controls.minAzimuthAngle = 0
controls.maxAzimuthAngle = 1.8

// ─── РЕНДЕРЕР ─────────────────────────────────

const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false,
    preserveDrawingBuffer: true,
    alpha: false,
    powerPreference: 'high-performance'
})

renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(pixelRatio)
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 1.0
renderer.outputColorSpace = THREE.SRGBColorSpace

// ✏️ Фон прямо на сцене — это единственный надёжный способ с composer
scene.background = new THREE.Color(0x808080)

// ─── HDR ENVIRONMENT ──────────────────────────

const pmremGenerator = new THREE.PMREMGenerator(renderer)
pmremGenerator.compileEquirectangularShader()

new RGBELoader().load('./models/little_paris_eiffel_tower_2k.hdr', (texture) => {
    texture.mapping = THREE.EquirectangularReflectionMapping
    const envMap = pmremGenerator.fromEquirectangular(texture).texture
    scene.environment = envMap
    scene.environmentIntensity = 1.0
    texture.dispose()
    pmremGenerator.dispose()
})

// ─── ОСВЕЩЕНИЕ ────────────────────────────────

light(scene)

// ✏️ HemisphereLight — небо сверху тёплое, земля снизу нейтральная
// Это самый физически корректный способ равномерного освещения без теней
// Верхние грани = skyColor, нижние = groundColor → нет чёрных зон нигде
const hemiLight = new THREE.HemisphereLight(0xfff8f0, 0xd4cfc8, 1.2)
scene.add(hemiLight)

// Key — основной с тенями, даёт форму и тени на полу
const keyLight = new THREE.DirectionalLight(0xfff8f0, 1.5)
keyLight.position.set(4, 8, 5)
keyLight.castShadow = true
keyLight.shadow.mapSize.width = 2048
keyLight.shadow.mapSize.height = 2048
keyLight.shadow.camera.near = 0.5
keyLight.shadow.camera.far = 25
keyLight.shadow.camera.left = -7
keyLight.shadow.camera.right = 7
keyLight.shadow.camera.top = 7
keyLight.shadow.camera.bottom = -7
keyLight.shadow.bias = -0.0003
keyLight.shadow.normalBias = 0.02
keyLight.shadow.radius = 4
scene.add(keyLight)

// Fill — с противоположной стороны, убирает резкие тени
const fillLight = new THREE.DirectionalLight(0xfff0e8, 0.8)
fillLight.position.set(-3, 5, 4)
scene.add(fillLight)

// Тень на полу
const shadowPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(20, 20),
    new THREE.ShadowMaterial({ opacity: 0.10 })
)
shadowPlane.rotation.x = -Math.PI / 2
shadowPlane.position.y = -0.001
shadowPlane.receiveShadow = true
scene.add(shadowPlane)

// ─── POST-PROCESSING ──────────────────────────

const composer = new EffectComposer(renderer)
composer.setPixelRatio(pixelRatio)
composer.setSize(sizes.width, sizes.height)
composer.renderTarget1.samples = 8
composer.renderTarget2.samples = 8

composer.addPass(new RenderPass(scene, camera))
composer.addPass(new SMAAPass(sizes.width * pixelRatio, sizes.height * pixelRatio))
composer.addPass(new OutputPass())

// ─── ФОН для theme() ──────────────────────────
// theme() ожидает backgroundMaterial, передаём заглушку
const backgroundMaterial = {
    uniforms: {
        centerColor: { value: new THREE.Color(0x808080) },
        edgeColor: { value: new THREE.Color(0x808080) },
    }
}

theme(scene, backgroundMaterial)

// ─── МОДЕЛЬ ───────────────────────────────────

const sceneSettings = {
    panelColor: '#233225',
    wallColor: '#e5e2dc'
}

let panelMaterials = []
let wallMaterial = null
let model = null
let originalMaterial = null

gltfLoader.load('./models/green-kitchen.glb', async (gltf) => {
    model = gltf.scene

    model.traverse((child) => {
        if (child.name.toLowerCase().includes('straight')) {
            child.visible = false
            child.castShadow = false
        }

        if (child.isMesh) {
            child.castShadow = child.visible
            child.receiveShadow = true

            const materials = Array.isArray(child.material) ? child.material : [child.material]
            materials.forEach(mat => {
                const matName = mat.name.toLowerCase()
                const objName = child.name.toLowerCase()

                if (matName.includes('cabinetpanel')) {
                    mat.color.set(sceneSettings.panelColor)
                    mat.envMapIntensity = 1.0
                    mat.needsUpdate = true
                    if (!panelMaterials.includes(mat)) panelMaterials.push(mat)
                }

                if (objName === 'wall' || matName === 'wall') {
                    wallMaterial = mat
                    wallMaterial.color.set(sceneSettings.wallColor)
                }
            })

            if (child.material.name === 'woodPart') {
                originalMaterial = child.material
            }
        }
    })

    model.position.y = 0
    scene.add(model)


    options(model, originalMaterial, scene, camera)
    await pdfCreate(renderer, camera, scene, model, backgroundMaterial)

    initAIImageGenerator({
        renderer,
        camera,
        scene,
    });
})

// ─── RESIZE ───────────────────────────────────

window.addEventListener('resize', () => {
    sizes.width = canvasContainer.clientWidth
    sizes.height = canvasContainer.clientHeight
    const pr = Math.min(window.devicePixelRatio, 2)

    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(pr)
    composer.setSize(sizes.width, sizes.height)
    composer.setPixelRatio(pr)
    composer.renderTarget1.samples = 8
    composer.renderTarget2.samples = 8
})

// ─── АНИМАЦИЯ ─────────────────────────────────

const tick = () => {
    controls.update()
    composer.render()
    window.requestAnimationFrame(tick)
}

tick()